<template>
  <ul :class="$style.collapsibleList">
    <li v-if="isHouseholdInsuranceIntegrationEnabled">
      <PartnerCollapsibleWidget
        v-if="shouldShowMobiliarHouseholdInsuranceWidget"
        id="mobiliar"
        :headerTitle="t(`integrations.mobiliar.title.${offerType.toLowerCase()}`)"
        :partnerHeaderText="t('integrations.partnerCollapsibleContainer.footer')"
        :partnerHeaderLogo="mobiliarLogo"
        @trackExpandWidget="trackExpandMobiliarHouseholdInsuranceWidget"
      >
        <template #component>
          <MobiliarHouseholdInsuranceWidgetContent
            :listingOfferType="offerType"
            @trackPartnerLink="trackPartnerLinkFunction"
          />
        </template>
      </PartnerCollapsibleWidget>
    </li>
    <li v-if="isISPIntegrationEnabled">
      <PartnerCollapsibleWidget
        id="sunrise"
        :headerTitle="t('sunrise.accordion.title')"
        :footerText="t('integrations.partnerCollapsibleContainer.footer')"
        :partnerLogo="ISPPromotionSunriseLogo"
        @trackExpandWidget="trackExpandSunriseISPWidget"
        @mounted="trackSunriseISPWidgetDisplayed"
      >
        <template #component>
          <ISPPromotionWidgetContent @trackPartnerLink="trackSunriseISPWidgetLearnMoreClick" />
        </template>
      </PartnerCollapsibleWidget>
    </li>

    <li v-if="isGgaMaurIntegrationEnabled">
      <PartnerCollapsibleWidget
        id="ggaMaur"
        headerTitle="Regionales Internet im neuen Zuhause"
        :footerText="t('integrations.partnerCollapsibleContainer.footer')"
        :partnerLogo="GgaMaurLogo"
        @trackExpandWidget="trackExpandGgaMaurWidget"
      >
        <template #component>
          <GgaMaurWidgetContent />
        </template>
      </PartnerCollapsibleWidget>
    </li>
  </ul>
</template>

<script lang="ts" setup>
import { Country, Listing, OfferType } from '@smg-real-estate/hg-rets-schema';
import { computed, defineAsyncComponent, ref } from 'vue';

import GgaMaurLogo from '@/components/PartnerIntegrations/GgaMaur/assets/logo_gga_maur.png?url';
import GgaMaurWidgetContent from '@/components/PartnerIntegrations/GgaMaur/GgaMaurWidgetContent.vue';
import ISPPromotionSunriseLogo from '@/components/PartnerIntegrations/ISPPromotion/assets/isp_promotion_sunrise_logo.png?url';
import PartnerCollapsibleWidget from '@/components/PartnerIntegrations/PartnerCollapsibleWidget/PartnerCollapsibleWidget.vue';
import {
  InteractionContainerDetail,
  InteractionContainerName,
  InteractionGoal,
} from '@/helpers/GTM/GTMParameters';

import { HgSupportedLanguage } from '@smg-real-estate/hg-search-libs';
import { useI18n } from 'vue-i18n';
import {
  trackExpandMobiliarHouseholdInsuranceWidget,
  trackExpandSunriseISPWidget,
  trackPartnerLink,
  trackPartnerTooltip,
  trackSunriseISPWidgetDisplayed,
  trackSunriseISPWidgetLearnMoreClick,
} from '../functions/tracking';

const { t, locale } = useI18n();

defineOptions({
  name: 'PartnerCollapsibleContainer',
});

const MobiliarHouseholdInsuranceWidgetContent = defineAsyncComponent(
  () =>
    import('@/components/PartnerIntegrations/MobiliarHouseholdInsurance/MobiliarHouseholdInsuranceWidgetContent.vue'),
);
const ISPPromotionWidgetContent = defineAsyncComponent(
  () => import('@/components/PartnerIntegrations/ISPPromotion/ISPPromotionWidgetContent.vue'),
);

interface Props {
  listing: Listing;
  isGgaMaurIntegrationEnabled?: boolean;
  isHouseholdInsuranceIntegrationEnabled?: boolean;
  isISPIntegrationEnabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  isGgaMaurIntegrationEnabled: false,
  isHouseholdInsuranceIntegrationEnabled: false,
  isISPIntegrationEnabled: false,
});

const mobiliarLogo = ref<string | undefined>(undefined);

const offerType = computed<OfferType>(() => {
  return props.listing.offerType || OfferType.Rent;
});
const shouldShowMobiliarHouseholdInsuranceWidget = computed<boolean>(() => {
  return props.listing?.address?.country === Country.Ch;
});

const trackExpandGgaMaurWidget = (): void => {
  trackPartnerTooltip(
    InteractionGoal.OPEN_DETAILS,
    InteractionContainerName.GGA_MAUR,
    InteractionContainerDetail.INTERNET_PROVIDER,
  );
};
const trackPartnerLinkFunction = (externalUrl: string, interactionGoal: InteractionGoal): void => {
  trackPartnerLink(externalUrl, interactionGoal, props.listing.id);
};
const setMobiliarLogo = async (): Promise<void> => {
  const mobiliarLogoMap: Record<HgSupportedLanguage, () => Promise<{ default: string }>> = {
    [HgSupportedLanguage.DE]: () => import('./assets/mobiliar_logo_de.png'),
    [HgSupportedLanguage.IT]: () => import('./assets/mobiliar_logo_it.png'),
    [HgSupportedLanguage.FR]: () => import('./assets/mobiliar_logo_fr.png'),
    [HgSupportedLanguage.EN]: () => import('./assets/mobiliar_logo_fr.png'),
  };

  const selectedLogo = await mobiliarLogoMap[locale.value as HgSupportedLanguage]();
  mobiliarLogo.value = selectedLogo.default;
};

setMobiliarLogo();
</script>

<style lang="scss" module>
@import '@/assets/variables';

$logo-max-width: 6.25rem;
$logo-max-height: 2.5rem;

.collapsibleList {
  display: flex;
  flex-direction: column;
  gap: $space-50;
  padding: 0;
  margin: $space-150 0;
  list-style: none;
}

.logo {
  max-width: $logo-max-width;
  max-height: $logo-max-height;
}
</style>
